import FilterForm from 'components/forms/FilterForm';
import {
  ATTEMPTS_STATUS_MAP,
  translateAssignmentAttemptStatus
} from 'services/assignments';
import { CrudFilters, HttpError } from '@refinedev/core';
import {
  Empty,
  Form,
  Input,
  Radio,
  RadioChangeEvent,
  Select,
  Space,
  Table
} from 'antd';
import { IAssignmentAttempt } from 'interfaces/assignments';
import useSelectCourseByType from 'hooks/useSelectCourseByType';
import { ListLayout } from 'components/layout/layout/ListLayout';
import {
  ShowButton,
  TagField,
  TextField,
  useTable
} from '@refinedev/antd';
import { formatDate } from '../../../services/date';
import { tagColorByStatus } from 'utils/statusColorMapping';
import { useCallback, useEffect, useState } from 'react';

const DEFAULT_PARENT_TYPE = 'Graduate::Discipline';

export const AssignmentAttemptList = () => {
  const [parentType, setParentType] = useState(DEFAULT_PARENT_TYPE);
  const { selectProps, selectedCourseType, setSelectedCourseType } =
    useSelectCourseByType(DEFAULT_PARENT_TYPE, 'graduate_disciplines');

  const { tableProps, searchFormProps } = useTable<
    IAssignmentAttempt,
    HttpError,
    {
      assignment_name: string;
      parent_id: string;
      parent_type: string;
      status: string;
      user_name: string;
      user_email: string;
    }
  >({
    onSearch: ({
      assignment_name,
      parent_id,
      parent_type,
      status,
      user_name,
      user_email
    }) => {
      setParentType(parent_type || DEFAULT_PARENT_TYPE);
      const filters: CrudFilters = [];

      filters.push(
        {
          field: 'filter[by_parent_id]',
          operator: 'eq',
          value: parent_id
        },
        {
          field: 'filter[by_parent_type]',
          operator: 'eq',
          value: parent_type
        },
        {
          field: 'filter[status]',
          operator: 'eq',
          value: status
        },
        {
          field: 'filter[search_name]',
          operator: 'eq',
          value: assignment_name
        },
        {
          field: 'filter[by_user_name]',
          operator: 'eq',
          value: user_name
        },
        {
          field: 'filter[by_user_email]',
          operator: 'eq',
          value: user_email
        }
      );

      return filters;
    },

    filters: {
      initial: [
        {
          field: 'filter[by_parent_type]',
          operator: 'eq',
          value: 'Graduate::Discipline'
        }
      ]
    }
  });

  const watchedParentType = Form.useWatch('parent_type', searchFormProps.form);

  useEffect(() => {
    if (
      watchedParentType === 'Course' ||
      watchedParentType === 'Graduate::Discipline'
    ) {
      setSelectedCourseType(watchedParentType);
    }
  }, [watchedParentType, setSelectedCourseType]);

  const onChangeType = useCallback(
    (e: RadioChangeEvent) => {
      if (searchFormProps.form?.getFieldValue('parent_id')) {
        searchFormProps.form.setFieldsValue({ parent_id: undefined });
      }

      setSelectedCourseType(e.target.value);
    },
    [searchFormProps.form, setSelectedCourseType]
  );

  return (
    <ListLayout
      title="Histórico de Atividades"
      createButtonProps={{
        hidden: true
      }}
    >
      <FilterForm searchFormProps={searchFormProps}>
        <Space className="mb-0 bg-slate-100 rounded-md p-2">
          <Space align="center" size="small">
            <span className="text-gray-700">Tipo de curso:</span>
            <Form.Item
              name="parent_type"
              className="mb-0"
              initialValue={DEFAULT_PARENT_TYPE}
            >
              <Radio.Group onChange={onChangeType}>
                <Radio.Button value="Course">Curso</Radio.Button>
                <Radio.Button value="Graduate::Discipline">
                  Disciplina
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Space>

          <Form.Item name="parent_id" className="mb-0 w-60">
            <Select
              key={selectedCourseType}
              placeholder={
                selectedCourseType === 'Course'
                  ? 'Busque por curso'
                  : 'Busque por disciplina'
              }
              allowClear
              {...selectProps}
              notFoundContent={
                <Empty
                  description={`${
                    selectedCourseType === 'Course'
                      ? 'Nenhum curso encontrado'
                      : 'Nenhuma disciplina encontrada'
                  }. Tente refazer sua busca com outros termos`}
                />
              }
            />
          </Form.Item>
        </Space>

        <Form.Item name="assignment_name" className="mb-0 w-64">
          <Input placeholder="Nome da atividade" />
        </Form.Item>

        <Form.Item name="user_name" className="mb-0 w-64">
          <Input placeholder="Nome do aluno" />
        </Form.Item>

        <Form.Item name="user_email" className="mb-0 w-64">
          <Input placeholder="E-mail do aluno" />
        </Form.Item>

        <Form.Item name="status" className="mb-0 w-44" initialValue={null}>
          <Select placeholder="Status da atividade" allowClear>
            {[...ATTEMPTS_STATUS_MAP].map(item => (
              <Select.Option key={item[0]} value={item[0]}>
                {item[1]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </FilterForm>

      <Table {...tableProps} rowKey="id">
        <Table.Column
          dataIndex="assignment_name"
          key="assignment_name"
          title="Nome da Atividade"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="course_name"
          key="course_name"
          title={
            parentType === 'Course' ? 'Nome do Curso' : 'Nome da Disciplina'
          }
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="user"
          key="user"
          title="Aluno"
          render={(user: IAssignmentAttempt['user']) => (
            <Space direction="vertical" size={0}>
              <TextField value={user?.full_name_or_name ?? '--'} />
              {user?.email && (
                <TextField
                  style={{ fontSize: 12, color: '#8c8c8c' }}
                  value={`E-mail: ${user.email}`}
                />
              )}
            </Space>
          )}
        />

        <Table.Column
          dataIndex="created_at"
          key="created_at"
          title="Iniciada em"
          width={110}
          render={value =>
            value ? <TextField value={formatDate(value, 'DD/MM/YYYY')} /> : '--'
          }
        />
        <Table.Column
          dataIndex="evaluated_at"
          key="evaluated_at"
          title="Avaliada em"
          width={110}
          render={value =>
            value ? <TextField value={formatDate(value, 'DD/MM/YYYY')} /> : '--'
          }
        />
        <Table.Column
          dataIndex="evaluator"
          key="evaluator"
          title="Avaliada por"
          render={value => (value ? <TextField value={value.name} /> : '--')}
        />
        <Table.Column
          dataIndex="status"
          key="status"
          title="Status"
          render={value => (
            <TagField
              color={tagColorByStatus(value)}
              value={translateAssignmentAttemptStatus(value)}
            />
          )}
        />
        <Table.Column<IAssignmentAttempt>
          title="Ações"
          dataIndex="actions"
          align="center"
          width={80}
          render={(_, record) => {
            return (
              <Space>
                <ShowButton hideText size="small" recordItemId={record.id} />
              </Space>
            );
          }}
        />
      </Table>
    </ListLayout>
  );
};
